<template>
  <v-container class="pa-0">
    <v-row dense row wrap justify="center">
      <v-col class="col-12">
        <search-verified-info></search-verified-info>
      </v-col>
    </v-row>
    <v-row
      v-if="isVerifiedEventsHeatmapUnlocked"
      dense
      row
      wrap
      justify="center"
    >
      <v-col dense class="col-12">
        <div class="map-type-toggle">
          <v-btn-toggle
            mandatory
            v-model="localMapType"
            @change="handleChangeMapType"
            dense
          >
            <v-btn x-small value="marker">Markers</v-btn>
            <v-btn x-small value="heatmap">Heatmap</v-btn>
          </v-btn-toggle>
        </div>
      </v-col>
    </v-row>
    <v-row dense row wrap justify="center">
      <v-col class="col-12">
        <v-btn
          @click="handleChangeVerifiedType('security')"
          small
          depressed
          :color="verifiedType === 'security' ? 'primary' : ''"
          title="Tile view"
        >
          <img
            class="event-type-icon"
            :class="verifiedType === 'security' ? 'white-icon' : ''"
            v-if="politicalIconSrc"
            :src="politicalIconSrc"
          />
          Security
        </v-btn>

        <v-btn
          @click="handleChangeVerifiedType('maritime')"
          :color="verifiedType === 'maritime' ? 'primary' : ''"
          small
          depressed
          title="Tile view"
          style="float: right;"
        >
          <img
            class="event-type-icon"
            :class="verifiedType === 'maritime' ? 'white-icon' : ''"
            v-if="maritimeIconSrc"
            :src="maritimeIconSrc"
          />
          Maritime
        </v-btn>
      </v-col>
    </v-row>
    <v-row dense row wrap justify="center">
      <v-col class="col-12">
        <EventsPickerV3
          v-if="!!verifiedType"
          :event-type="verifiedType"
          :topic-filter-counts="topicFilterCounts"
          :reset="reset"
          :selected-topics="selectedTopics"
          @change="handleChangeEvents"
          @change-has-topics-selected="handleChangeHasTopicsSelected"
        ></EventsPickerV3>

        <div class="clearfix"></div>
      </v-col>
    </v-row>
    <v-row v-if="securitySelected">
      <v-col>
        <IncidentsSectorSelector
          label="Sector"
          :selected="selectedSectors"
          :reset="reset"
          @change="handleChangeSector"
        ></IncidentsSectorSelector>
      </v-col>
    </v-row>
    <v-row v-if="securitySelected">
      <v-col>
        <IncidentsAssetSelector
          label="Asset Type"
          :disabled="false"
          :selected="selectedAssets"
          :reset="reset"
          @change="handleChangeAsset"
        ></IncidentsAssetSelector>
      </v-col>
    </v-row>
    <v-row v-if="securitySelected">
      <v-col>
        <IncidentsSeveritySlider
          :selected="selectedSeverityRange"
          @change="handleChangeSeverity"
        ></IncidentsSeveritySlider>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashFind from "lodash/find";

import { getTopicKey } from "@/services/events/topics.service";

const EMITTED_ACTIONS = {
  CHANGE: "change",
  CHANGE_MAP_TYPE: "change-map-type",
};

const EVENT_PICKER_ACTIONS = {
  CHANGED_LIVE_SEARCH: "changed-live-search",
  CHANGE_HAS_TOPICS_SELECTED: "change-has-topics-selected",
};

export default {
  name: "SearchFilterEventsVerified",
  components: {
    IncidentsSectorSelector: () =>
      import("@/components/controlRisks/incidents/IncidentsSectorSelector"),
    IncidentsAssetSelector: () =>
      import("@/components/controlRisks/incidents/IncidentsAssetSelector"),
    IncidentsSeveritySlider: () =>
      import("@/components/controlRisks/incidents/IncidentsSeveritySlider"),
    EventsPickerV3: () => import("@/components/events/EventsPickerV3"),
    SearchVerifiedInfo: () => import("@/components/search/SearchVerifiedInfo"),
  },
  props: {
    selectedTopics: {
      type: String,
    },
    primaryTopics: {
      type: Array,
    },
    verifiedTypeSelection: {
      type: String,
    },
    verifiedExtrasSelection: {
      type: Object,
    },
    topicFilterCounts: {
      type: Object,
    },
    reset: {
      type: String,
    },
    mapType: {
      type: String,
      default: "marker",
    },
  },
  data() {
    return {
      selectedPrimaryTopicType: null,
      topics: null,
      subtopics: null,
      verifiedType: null,
      localMapType: null,
    };
  },
  mounted() {
    if (this.verifiedTypeSelection === "security") {
      this.handleChangeVerifiedType("security");
    } else if (this.verifiedTypeSelection === "maritime") {
      this.handleChangeVerifiedType("maritime");
    }

    if (this.mapType) {
      this.localMapType = this.mapType;
    }
  },
  computed: {
    selectedSectors() {
      if (lodashGet(this.verifiedExtrasSelection, "sectors")) {
        return this.verifiedExtrasSelection.sectors;
      }
      return null;
    },
    selectedAssets() {
      if (lodashGet(this.verifiedExtrasSelection, "assets")) {
        return this.verifiedExtrasSelection.assets;
      }
      return null;
    },
    selectedSeverityRange() {
      if (
        lodashHas(this.verifiedExtrasSelection, "severityMin") &&
        lodashHas(this.verifiedExtrasSelection, "severityMax")
      ) {
        return [
          this.verifiedExtrasSelection.severityMin,
          this.verifiedExtrasSelection.severityMax,
        ];
      }
      return null;
    },
    securitySelected() {
      return this.verifiedType === "security";
    },
    maritimeIconSrc() {
      return require("@/assets/images/icons/controlRisksIncidents/maritime/small/maritime.svg");
    },
    politicalIconSrc() {
      return require("@/assets/images/icons/controlRisksIncidents/security-black.svg");
    },
    isVerifiedEventsHeatmapUnlocked() {
      return this.featureUnlocked("verified-events-heatmap");
    },
  },
  methods: {
    getTopics() {
      if (!this.selectedTopics) {
        this.topics = this.primaryTopics;
        return;
      }
      if (this.primaryTopics) {
        if (this.selectedPrimaryTopicType) {
          const key = getTopicKey(this.selectedPrimaryTopicType, true);
          const topic = lodashFind(this.primaryTopics, ["apiName", key]);
          if (topic && topic.subtopics) {
            this.topics = topic.subtopics;
            return;
          }
        }
        this.topics = this.primaryTopics;
      }
    },
    handleClickEventsBtn(topic, selected) {
      this.$emit(EMITTED_ACTIONS.CHANGE, { topic, selected });
    },
    handleClickType() {
      this.selectedPrimaryTopicType = null;
      this.getTopics();
    },
    handleClickPrimaryTopic(topic) {
      if (topic && topic.type) {
        this.selectedPrimaryTopicType = topic.type;
        this.getTopics();
      }
    },
    handleChangeSector(sectors) {
      this.$emit(EMITTED_ACTIONS.CHANGE, { sectors });
    },
    handleChangeAsset(assets) {
      this.$emit(EMITTED_ACTIONS.CHANGE, { assets });
    },
    handleChangeSeverity(severity) {
      this.$emit(EMITTED_ACTIONS.CHANGE, { severity });
    },
    handleChangeEvents(event) {
      this.$emit(EMITTED_ACTIONS.CHANGE, event);
    },
    handleChangeHasTopicsSelected(val) {
      this.$emit(EVENT_PICKER_ACTIONS.CHANGE_HAS_TOPICS_SELECTED, val);
    },
    handleChangeVerifiedType(val) {
      if (this.verifiedType === val) {
        val = null;
      }
      this.verifiedType = val;
      //TODO need to update events to get rid of events that aren't
      this.$emit(EMITTED_ACTIONS.CHANGE, { verifiedType: val });
    },
    handleChangeMapType(val) {
      this.$emit(EMITTED_ACTIONS.CHANGE_MAP_TYPE, val);
    },
  },
  watch: {
    selectedTopics: {
      handler() {
        this.getTopics();
      },
      deep: true,
      immediate: true,
    },
    verifiedTypeSelection: {
      handler() {
        //TODO instead of these ifs just have handleChanbeVerifiedType handle it?
        //also probably have it hndle null instead of whatever this does currently
        if (this.verifiedTypeSelection === "security") {
          this.handleChangeVerifiedType("security");
        } else if (this.verifiedTypeSelection === "maritime") {
          this.handleChangeVerifiedType("maritime");
        }
      },
    },
    mapType: {
      handler(val) {
        this.localMapType = this.mapType;
      },
    },
  },
};
</script>

<style scoped>
.event-type-icon {
  height: 2em;
}
.white-icon {
  -webkit-filter: invert(1);
  filter: invert(1);
}
.map-type-toggle {
  text-align: center;
}
</style>
